import { ClinicalTimeField, localDateTime, toOccurredAt } from './ClinicalEventEntry';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FieldGroup } from '@/components/ui/field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { newbornExamItems, examPayload, type ExamDraft } from './postpartumFields';
import { validatePayload } from './validate';
import type { ProposedEntry } from './PrenatalEntry';

export function deviceDateTime(date = new Date()) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
}
export function NewbornExamEntry({
  subjectId,
  subjectLabel,
  timeZone,
  disabled,
  onSave,
}: {
  subjectId: string;
  subjectLabel: string;
  timeZone?: string;
  disabled?: boolean;
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState<ExamDraft>({});
  const [time, setTime] = useState(() => localDateTime(new Date(), timeZone));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const update = (item: string, change: Partial<ExamDraft[string]>) =>
    setDraft((current) => ({ ...current, [item]: { ...(current[item] ?? { assessment: '', note: '' }), ...change } }));
  async function save() {
    if (disabled || saving) return;
    let occurredAt: string;
    try {
      occurredAt = toOccurredAt(time, timeZone);
    } catch {
      setError('Enter a valid clinical time.');
      return;
    }
    const payload = examPayload(draft);
    if (!Array.isArray(payload.findings) || !payload.findings.length) {
      setError('Record at least one exam finding.');
      return;
    }
    const issues = validatePayload('NB.EXAM', payload);
    if (issues.length) {
      setError(issues.join('; '));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave([{ event_type: 'NB.EXAM', subject_id: subjectId, payload }], occurredAt);
      setDraft({});
      setTime(localDateTime(new Date(), timeZone));
    } catch {
      setError('The exam was not saved. Your findings are retained; retry when ready.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <FieldGroup>
      <ClinicalTimeField value={time} onChange={setTime} timeZone={timeZone} disabled={disabled || saving} />
      <p>Newborn exam for {subjectLabel}. Only findings you enter are saved.</p>
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        disabled={disabled || saving}
        onClick={() =>
          setDraft((current) =>
            Object.fromEntries(
              newbornExamItems.map(([, item]) => [
                item,
                {
                  assessment: current[item]?.assessment || (current[item]?.note.trim() ? '' : 'Normal'),
                  note: current[item]?.note || '',
                },
              ]),
            ),
          )
        }>
        Mark unrecorded items normal after assessment
      </Button>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Assessment</TableHead>
            <TableHead>Finding</TableHead>
            <TableHead>Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {newbornExamItems.map(([, item]) => (
            <TableRow key={item}>
              <TableCell>{item}</TableCell>
              <TableCell>
                <Select
                  value={draft[item]?.assessment || 'unrecorded'}
                  onValueChange={(value) =>
                    update(item, {
                      assessment: value === 'unrecorded' ? '' : (value as ExamDraft[string]['assessment']),
                    })
                  }
                  disabled={disabled || saving}>
                  <SelectTrigger className="min-h-11" aria-label={`${item} assessment`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {['unrecorded', 'Normal', 'Abnormal', 'Not assessed'].map((value) => (
                        <SelectItem key={value} value={value}>
                          {value === 'unrecorded' ? 'Not recorded' : value}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Input
                  className="min-h-11"
                  aria-label={`${item} note`}
                  value={draft[item]?.note || ''}
                  onChange={(e) => update(item, { note: e.target.value })}
                  disabled={disabled || saving}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button className="min-h-11" disabled={disabled || saving} onClick={save}>
        {saving ? 'Saving exam…' : 'Save newborn exam'}
      </Button>
    </FieldGroup>
  );
}
